/**
 * spawnEntity — the LevelEntity → Entity factory (Brief 007 spine).
 *
 * World calls this once per `level.entities[]` member at build (and again at
 * respawn, for deterministic replay-safe restarts). It owns the tile→px spawn-point
 * convention (cx = column center, feetY = bottom of the entity's tile cell), then
 * switches on type. PLAYER_START / GOAL_FLAG return null (World still owns those
 * directly). SPIKE / COIN / FLYER return null for now — P2 fills them by APPENDING
 * a case below; the switch is structured so P2 is pure-append (no edits to existing
 * cases).
 */
import type { Entity } from '../Entity';
import { EntityType, type LevelEntity } from '../LevelFormat';
import { Goomba } from './Goomba';
import { Spike } from './Spike';
import { Coin } from './Coin';
import { Flyer } from './Flyer';
import { Walker } from './Walker';
import { Hopper } from './Hopper';
import { Ambush } from './Ambush';
import { QBlock } from './QBlock';
import { Brick } from './Brick';
import { Spring } from './Spring';
import { Item } from './Item';
import { Platform } from './Platform';
import { Conveyor } from './Conveyor';
import { Warp } from './Warp';
import { Lava } from './Lava';
import { Saw } from './Saw';
import { FireBar } from './FireBar';
import { Thwomp } from './Thwomp';
import { Checkpoint } from './Checkpoint';
import { Crumble } from './Crumble';
import { Launchpad } from './Launchpad';
import { Bumper } from './Bumper';
import { DreamBlock } from './DreamBlock';
import { ExitSwitch } from './ExitSwitch';
import { ExitGate } from './ExitGate';
import { Fan } from './Fan';
import { Laser } from './Laser';
import { Mine } from './Mine';
import { CrackedWall } from './CrackedWall';
import { MoveBlock } from './MoveBlock';
import { Projectile } from './Projectile';
import { Gunner } from './Gunner';
import { Armored } from './Armored';
import { Turret } from './Turret';
import { Spawner } from './Spawner';
import { Strafer } from './Strafer';
import { Lobber } from './Lobber';

/**
 * Build a simulated Entity for a placed LevelEntity, or null when the type is owned
 * by World directly (PLAYER_START / GOAL_FLAG). `ts` is the level's tileSize. Resolves
 * the tile→px spawn-point convention, then delegates to {@link spawnEntityAtPx}.
 */
export function spawnEntity(le: LevelEntity, ts: number): Entity | null {
  const cx = le.x * ts + ts / 2;
  const feetY = (le.y + 1) * ts;
  return spawnEntityAtPx(le.type, cx, feetY, ts, le.props);
}

/**
 * Core factory keyed by world-px spawn point (`cx` = center-x, `feetY` = bottom of the
 * entity's cell). Used both by {@link spawnEntity} (level load / respawn) and by
 * World.spawnEntityAt (Brief 009 runtime emission — a `?`-block popping a COIN). `ts`
 * is the level's tileSize (tile-aligned blocks size their cell from it). Returns null
 * for World-owned or unknown types so an unhandled type never crashes.
 */
export function spawnEntityAtPx(
  type: EntityType,
  cx: number,
  feetY: number,
  ts: number,
  props?: Record<string, unknown>,
): Entity | null {
  switch (type) {
    case EntityType.GOOMBA:
      return new Goomba(cx, feetY, props);

    // World owns these directly (start position / goal box) — no Entity needed.
    case EntityType.PLAYER_START:
    case EntityType.GOAL_FLAG:
      return null;

    // POLISH_02 #6: render-only decoration — World draws it straight from level.entities[]
    // (see World.buildDecorSprites), so the sim spawns NOTHING for it. No body, no update,
    // no collision, excluded from every entity-iteration pass by construction (it never
    // enters Sim.entities).
    case EntityType.DECOR:
      return null;

    // P2: behaviour wired (Brief 006). Each type is now fully simulated.
    case EntityType.SPIKE: return new Spike(cx, feetY, props);
    case EntityType.COIN:  return new Coin(cx, feetY, props);
    case EntityType.FLYER: return new Flyer(cx, feetY, props);

    // Brief 008 archetypes (pure-append). variant/tuning ride in props.
    case EntityType.WALKER: return new Walker(cx, feetY, props);
    case EntityType.HOPPER: return new Hopper(cx, feetY, props);
    case EntityType.AMBUSH: return new Ambush(cx, feetY, props);

    // Brief 009 interactive blocks — SOLID, tile-aligned 1×1 cell (need `ts`).
    case EntityType.QBLOCK: return new QBlock(cx, feetY, props, ts);
    case EntityType.BRICK:  return new Brick(cx, feetY, props, ts);
    case EntityType.SPRING: return new Spring(cx, feetY, props, ts);

    // Brief 010 power-up pickup — overlap-collect; props.kind selects the effect.
    case EntityType.ITEM:   return new Item(cx, feetY, props);

    // Brief 011 terrain mechanisms — kinematic solids + paired teleporters.
    case EntityType.PLATFORM: return new Platform(cx, feetY, props, ts);
    case EntityType.CONVEYOR: return new Conveyor(cx, feetY, props, ts);
    case EntityType.WARP:     return new Warp(cx, feetY, props, ts, 'warp');
    case EntityType.DOOR:     return new Warp(cx, feetY, props, ts, 'door');

    // Brief 012 hazards. LAVA/SAW/THWOMP are single bodies; FIREBAR is an invisible
    // pivot that expands into its orbiting fire-orb segments via spawnChildren().
    case EntityType.LAVA:    return new Lava(cx, feetY, props, ts);
    case EntityType.SAW:     return new Saw(cx, feetY, props, ts);
    case EntityType.FIREBAR: return new FireBar(cx, feetY, props, ts);
    case EntityType.THWOMP:  return new Thwomp(cx, feetY, props, ts);

    // Brief 013 checkpoint — non-solid overlap entity; overrides the respawn point.
    case EntityType.CHECKPOINT: return new Checkpoint(cx, feetY, props);

    // Brief 014 variant-object kit — shared per-archetype objects. Crumble/Launchpad/
    // DreamBlock/ExitGate are solid (1×1 or multi-cell); Bumper/ExitSwitch/Fan/Laser are
    // non-solid overlap. ExitSwitch/ExitGate pair on props.linkId via the World latch.
    case EntityType.CRUMBLE:    return new Crumble(cx, feetY, props, ts);
    case EntityType.LAUNCHPAD:  return new Launchpad(cx, feetY, props, ts);
    case EntityType.BUMPER:     return new Bumper(cx, feetY, props, ts);
    case EntityType.DREAMBLOCK: return new DreamBlock(cx, feetY, props, ts);
    case EntityType.EXITSWITCH: return new ExitSwitch(cx, feetY, props, ts);
    case EntityType.EXITGATE:   return new ExitGate(cx, feetY, props, ts);
    case EntityType.FAN:        return new Fan(cx, feetY, props, ts);
    case EntityType.LASER:      return new Laser(cx, feetY, props, ts);

    // Brief 014 P1 — mine (instakill dot), attack-breakable wall, dash-launched block.
    // Gold/Ring (COIN variant), Note block (SPRING variant), Falling platform (PLATFORM
    // variant) reuse their existing cases via props — no new factory case needed.
    case EntityType.MINE:        return new Mine(cx, feetY, props, ts);
    case EntityType.CRACKEDWALL: return new CrackedWall(cx, feetY, props, ts);
    case EntityType.MOVEBLOCK:   return new MoveBlock(cx, feetY, props, ts);

    // RUN-AND-GUN enemy roster (step 3) — ranged shooter + heavy soak; reuse the Walker ground
    // spine. GUNNER fires an enemy PROJECTILE; ARMORED carries hp (props.hp, default 3).
    case EntityType.GUNNER:   return new Gunner(cx, feetY, props);
    case EntityType.ARMORED:  return new Armored(cx, feetY, props);

    // RUN-AND-GUN emplacements (step 4) — static aim-tracking turret + grunt spawner.
    case EntityType.TURRET:   return new Turret(cx, feetY, props);
    case EntityType.SPAWNER:  return new Spawner(cx, feetY, props);
    case EntityType.STRAFER:  return new Strafer(cx, feetY, props);
    case EntityType.LOBBER:   return new Lobber(cx, feetY, props);

    // RUN-AND-GUN bullet — RUNTIME-SPAWNED (never in level.entities[], no roster row). The
    // player's onShoot passes {vx,vy,life}; a GUNNER passes {…, owner:'enemy'} via spawnEntityAt.
    case EntityType.PROJECTILE:  return new Projectile(cx, feetY, props);

    default:
      return null;
  }
}

// Re-export the reward-token map (Brief 010) so callers that already import the spawn
// factory can reach it; the definition lives in ./rewards to avoid a QBlock↔factory cycle.
export { resolveRewardToken, type RewardSpec } from './rewards';
